import axios from 'axios';
import { getAccessTokenJWT } from 'utils/okta';
import { appConfig } from 'config/appConfig';
import { getExternalAccessTokenJWT, getExternalAccessStorage } from 'utils/externalAccess';
import { getCountry } from 'utils/country';
import { epochToISOFormat } from 'utils/date';
import okta from 'constants/okta';

const APIEndpoint = 'externalaccess';

export const get = async (accessKey, apiToken) => {
  const oktaOrExternalJwt = getAccessTokenJWT() || getExternalAccessTokenJWT();
  const token = apiToken || okta.Bearer + oktaOrExternalJwt;
  return axios({
    method: 'get',
    url: appConfig.API_URL + '/' + APIEndpoint,
    params: {
      accessKey: accessKey,
    },
    headers: {
      Authorization: token,
      'subscription-key': appConfig.API_SUBSCRIPTION_KEY,
    },
  })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error(error);
      throw error;
    });
};

export const post = async (naId, brokerId) => {
  const payload = {
    NaId: naId,
    BrokerId: brokerId,
  };

  return axios({
    method: 'post',
    url: appConfig.API_URL + '/' + APIEndpoint,
    data: payload,
    headers: {
      Authorization: okta.Bearer + getAccessTokenJWT(),
      'subscription-key': appConfig.API_SUBSCRIPTION_KEY,
    },
  })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error(error);
      throw Error(error);
    });
};

export const updateExternalConfig = async (payload) => {
  return axios({
    method: 'put',
    url: appConfig.API_URL + '/' + APIEndpoint,
    data: payload,
    headers: {
      Authorization: okta.Bearer + getAccessTokenJWT(),
      'subscription-key': appConfig.API_SUBSCRIPTION_KEY,
    },
  })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error(error);
      throw Error(error);
    });
};

export const getActivityLogs = async (accessKey) => {
  return axios({
    method: 'get',
    url: appConfig.API_URL + '/' + APIEndpoint + '/getallactivitylogs',
    params: {
      accesskey: accessKey,
    },
    headers: {
      Authorization: okta.Bearer + getAccessTokenJWT(),
      'subscription-key': appConfig.API_SUBSCRIPTION_KEY,
    },
  })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error(error);
      throw Error(error);
    });
};

export const getTokenByKey = async (key) => {
  return axios({
    method: 'post',
    url: appConfig.EXTERNAL_CLIENT_API_URL + '/gettokenbykey',
    data: JSON.stringify(key),
    headers: {
      'Content-Type': 'application/json',
      'subscription-key': appConfig.EXTERNAL_CLIENT_API_SUBSCRIPTION_KEY,
    },
  })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error(error);
      throw error;
    });
};

export const verifyCredentials = async (key, password) => {
  return axios({
    method: 'post',
    url: appConfig.API_URL + '/' + APIEndpoint + '/verifycredentials',
    data: {
      AccessKey: key,
      Password: password,
    },
    headers: {
      Authorization: okta.Bearer + getExternalAccessTokenJWT(),
      'subscription-key': appConfig.API_SUBSCRIPTION_KEY,
    },
  })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error(error);
      throw error;
    });
};

export const getNA = async (id, accessKey, apiToken) => {
  const token = apiToken || okta.Bearer + getExternalAccessTokenJWT();
  return axios({
    method: 'get',
    url: appConfig.API_URL + '/' + APIEndpoint + '/getneedsassessment',
    params: {
      id: id,
      country: getCountry(),
      accessKey: accessKey,
    },
    headers: {
      Authorization: token,
      'subscription-key': appConfig.API_SUBSCRIPTION_KEY,
    },
  })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error(error);
      return Promise.reject(error);
    });
};

export const saveNA = async (company, projectName, na, accessKey) => {
  const {
    leaseExpirationYear,
    size,
    headcount,
    inOffice,
    fullyRemote,
    hybrid,
    daysInOffice,
    defaultGrowth,
    startingYear,
    leaseTermInYears,
    spaces,
    growth,
    workplacestyleSelected,
    sharedSeatsRatio,
  } = na;

  const payload = {
    Name: projectName,
    CompanyName: company.name,
    CompanyEdpid: company.edpid,
    CompanyId: company.id,
    LeaseExpiration: leaseExpirationYear ? epochToISOFormat(leaseExpirationYear) : null,
    Size: size,
    StartingYear: epochToISOFormat(startingYear),
    LeaseTermYears: leaseTermInYears,
    Headcount: headcount,
    InOffice: inOffice,
    FullyRemote: fullyRemote,
    Hybrid: hybrid,
    DaysInOffice: daysInOffice,
    DefaultGrowth: defaultGrowth,
    WorkplaceStyleSelected: workplacestyleSelected,
    Workplaces: JSON.stringify(spaces),
    Growth: JSON.stringify(growth),
    SharedSeatsRatio: JSON.stringify(sharedSeatsRatio),
    Country: getCountry(),
    CreatedAt: epochToISOFormat(new Date(), true),
    LastStep: 1,
    ScoreCardReached: false,
  };

  return axios({
    method: 'post',
    url: appConfig.API_URL + '/' + APIEndpoint + '/createneedsassessment',
    data: payload,
    headers: {
      Authorization: okta.Bearer + getExternalAccessTokenJWT(),
      'subscription-key': appConfig.API_SUBSCRIPTION_KEY,
      accessKey: accessKey,
    },
  })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error(error);
      throw Error(error);
    });
};

export const updateExternalNA = async (na, accessKey) => {
  const {
    id,
    leaseExpirationYear,
    size,
    headcount,
    inOffice,
    fullyRemote,
    hybrid,
    daysInOffice,
    defaultGrowth,
    startingYear,
    leaseTermInYears,
    workplacestyleSelected,
    spaces,
    growth,
    anyonecanaccess,
    lastStep,
    scoreCardReached,
    sharedSeatsRatio,
  } = na;

  const payload = {
    Id: id,
    LeaseExpiration: leaseExpirationYear ? epochToISOFormat(leaseExpirationYear) : null,
    Size: size,
    StartingYear: epochToISOFormat(startingYear),
    LeaseTermYears: leaseTermInYears,
    Headcount: headcount,
    InOffice: inOffice,
    FullyRemote: fullyRemote,
    Hybrid: hybrid,
    DaysInOffice: daysInOffice,
    DefaultGrowth: defaultGrowth,
    WorkplaceStyleSelected: workplacestyleSelected,
    Workplaces: JSON.stringify(spaces),
    Growth: JSON.stringify(growth),
    SharedSeatsRatio: JSON.stringify(sharedSeatsRatio),
    Country: getCountry(),
    AnyoneCanAccess: anyonecanaccess,
    UpdatedAt: epochToISOFormat(new Date(), true),
    LastStep: lastStep,
    ScoreCardReached: scoreCardReached,
  };

  return axios({
    method: 'put',
    url: appConfig.API_URL + '/' + APIEndpoint + '/updateneedsassessment',
    data: payload,
    headers: {
      Authorization: okta.Bearer + getExternalAccessTokenJWT(),
      'subscription-key': appConfig.API_SUBSCRIPTION_KEY,
      accessKey: accessKey,
    },
  })
    .then((response) => response.data)
    .catch((error) => {
      // TO-DO. Logging
      // control 401. okta redirection
      console.error(error);
      throw Error(error);
    });
};

export const updateActivityLog = async ({ accessKey, na, type, updateLastUpdate }) => {
  return axios({
    method: 'post',
    url: appConfig.API_URL + '/' + APIEndpoint + '/logactivity',
    data: {
      ExternalAccessKey: accessKey,
      Type: type,
      ClientEmail: getExternalAccessStorage().clientEmail,
      CreatedAt: na.createdAt,
      UpdatedAt: updateLastUpdate ? epochToISOFormat(new Date(), true) : null,
      AccessedAt: epochToISOFormat(new Date(), true),
    },
    headers: {
      Authorization: okta.Bearer + getExternalAccessTokenJWT(),
      'subscription-key': appConfig.API_SUBSCRIPTION_KEY,
      accessKey: accessKey,
    },
  })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error(error);
      throw Error(error);
    });
};
